import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { friendlyError } from '../../utils/errorMessages';
import { trackEvent } from '../../hooks/useGoogleAnalytics';

const HERO_PLATFORMS = [
  { name: 'LinkedIn', icon: 'Linkedin', color: '#0A66C2', bg: '#0A66C215' },
  { name: 'X', icon: 'X', color: '#FFFFFF', bg: 'rgba(128,128,128,0.1)' },
];

const PLATFORMS_ALL = [
  { name: 'LinkedIn', icon: 'Linkedin', color: '#0A66C2' },
  { name: 'X', icon: 'X', color: '#FFFFFF' },
  { name: 'Bluesky', icon: 'Bluesky', color: '#0085FF', comingSoon: true },
  { name: 'Mastodon', icon: 'Mastodon', color: '#6364FF', comingSoon: true },
];

const STEPS = [
  {
    num: 1,
    title: 'Dump Your Thoughts',
    desc: 'Paste rough notes, bullet points, or a brain dump. Include code snippets if relevant.',
    color: 'zinc',
    examples: ['"Refactored auth flow today"', '"Spent 3hrs on a typo — here\'s what I learned"', '"Why we chose event sourcing"'],
  },
  {
    num: 2,
    title: 'AI Refines It',
    desc: 'The AI structures your ideas into platform-ready posts while keeping your voice and technical accuracy.',
    color: 'purple',
    bullets: ['Keeps technical terms intact', 'Removes buzzword fluff', 'Optimizes for each platform'],
  },
  {
    num: 3,
    title: 'Publish Everywhere',
    desc: 'Preview, edit if needed, and publish to all your connected platforms with one click.',
    color: 'green',
    platforms: true,
  },
];

const COMPARISON = [
  { feature: 'AI Content Refinement', us: true, them: 'partial', desc: 'Paste rough notes — AI turns them into polished posts that still sound like you' },
  { feature: 'Platform-optimized formatting', us: true, them: false, desc: 'Auto-formats for LinkedIn and X character limits, hashtags, and engagement patterns' },
  { feature: 'One-click multi-platform publish', us: true, them: false, desc: 'Publish to LinkedIn and X simultaneously without copy-pasting' },
  { feature: 'Code snippet support', us: true, them: false, desc: 'Include code blocks that render properly on each platform' },
  { feature: 'Writing style presets', us: true, them: false, desc: 'Deep-Dive Dev, Mentor, Minimalist — or create your own tone' },
  { feature: 'Post analytics', us: true, them: false, desc: 'Track impressions, engagement, and what content performs best' },
  { feature: 'Preserves your voice', us: true, them: 'partial', desc: 'Keeps your technical terms and tone, not generic AI fluff' },
  { feature: 'Image attachments', us: true, them: false, desc: 'Upload and attach images directly to your posts for each platform' },
];

const ROLES = [
  'Developers', 'Founders', 'Product Managers', 'Architects', 'DevRels',
  'Engineering Managers', 'Data Scientists', 'Designers', 'CTOs', 'Job Seekers',
];

const PRICING_FEATURES = [
  { name: 'AI Content Generation', free: '5/month', pro: '100/month' },
  { name: 'Publish to LinkedIn & X', free: '3/month', pro: 'Unlimited' },
  { name: 'Image Attachments', free: false, pro: true },
  { name: 'Custom Writing Styles', free: '2 styles', pro: 'Unlimited' },
  { name: 'Analytics Dashboard', free: false, pro: true },
  { name: 'Post Scheduling', free: false, pro: 'coming soon' },
  { name: 'Priority Support', free: false, pro: true },
  { name: 'Early Access to New Features', free: false, pro: true },
];


const FAQS = [
  { q: 'Is POSTRequest free to use?', a: 'Yes. The Basic plan gives you content refinement and multi-platform publishing with monthly limits — no credit card required. Upgrade to Pro for more.' },
  { q: 'Which platforms are supported?', a: 'Currently LinkedIn and X (Twitter). Bluesky support is coming soon.' },
  { q: 'Will my posts sound like generic AI content?', a: 'No. POSTRequest preserves your voice. You provide the ideas — the AI handles structure and formatting while keeping your tone intact.' },
  { q: 'Can I edit the AI-generated content before publishing?', a: 'Absolutely. Every post goes through a side-by-side editor and preview where you can edit before it goes live.' },
  { q: 'Is my data safe?', a: 'Your data is stored securely with row-level security. We don\'t sell your data or use your content for training. OAuth-only — we never see your passwords.' },
];

const SHOWCASE_TABS = [
  { key: 'write', label: 'Write', icon: 'PenLine', img: '/assets/idea-input.png', alt: 'Paste rough ideas and generate platform-ready posts' },
  { key: 'edit', label: 'Edit & Preview', icon: 'Columns', img: '/assets/edit-preview.png', alt: 'Side-by-side editor with live preview for LinkedIn and X' },
  { key: 'track', label: 'Track', icon: 'BarChart3', img: '/assets/dashboard.png', alt: 'Analytics dashboard showing post performance' },
];

const ProductShowcase = () => {
  const [activeIdx, setActiveIdx] = useState(1); // start on edit & preview
  const timerRef = useRef(null);
  const INTERVAL = 4000;

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActiveIdx(i => (i + 1) % SHOWCASE_TABS.length), INTERVAL);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  const handleTabClick = (idx) => {
    setActiveIdx(idx);
    resetTimer();
  };

  const active = SHOWCASE_TABS[activeIdx];

  return (
    <section className="pb-20 pt-6">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tabs with progress indicator */}
        <div className="flex justify-center gap-2 mb-6">
          {SHOWCASE_TABS.map((tab, idx) => (
            <button
              key={tab.key}
              onClick={() => handleTabClick(idx)}
              className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all overflow-hidden ${
                activeIdx === idx
                  ? 'bg-primary text-white shadow-lg shadow-primary/25'
                  : 'bg-card text-muted-foreground border border-border hover:text-foreground hover:border-primary/30'
              }`}
            >
              {activeIdx === idx && (
                <motion.div
                  className="absolute bottom-0 left-0 h-0.5 bg-white/40"
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: INTERVAL / 1000, ease: 'linear' }}
                  key={`progress-${activeIdx}-${Date.now()}`}
                />
              )}
              <Icon name={tab.icon} size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Screenshot — fixed aspect ratio prevents layout shift */}
        <div className="rounded-2xl overflow-hidden border border-border shadow-2xl shadow-violet-500/10 relative bg-card" style={{ aspectRatio: '16 / 9' }}>
          <AnimatePresence mode="wait">
            <motion.img
              key={active.key}
              src={active.img}
              alt={active.alt}
              className="w-full h-full object-cover absolute inset-0"
              loading="eager"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            />
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const AuthenticationLanding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activePlatform, setActivePlatform] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const { scrollYProgress } = useScroll();
  const scrollTrackedRef = useRef({ 25: false, 50: false, 75: false, 100: false });

  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    const timer = setInterval(() => setActiveStep((s) => (s + 1) % 3), 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setActivePlatform((p) => (p + 1) % HERO_PLATFORMS.length), 2500);
    return () => clearInterval(timer);
  }, []);

  const handleStartPublishing = () => setShowLoginModal(true);

  const handleGoogleSignIn = async () => {
    setLoading(true); setError('');
    try {
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      const msg = friendlyError(err);
      setError(msg); toast.error(msg); setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted to-background">

      {/* ─── Navbar ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon name="Send" size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">POSTRequest</span>
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleStartPublishing}>
              Sign In
            </Button>
            <Button size="sm" onClick={handleStartPublishing}>
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <motion.section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-10 md:pt-32 md:pb-14"
        variants={containerVariants} initial="hidden" animate="visible"
      >
        <motion.div className="text-center max-w-3xl mx-auto" variants={itemVariants}>
          <motion.div className="flex justify-center gap-3 mb-8" variants={itemVariants}>
            {HERO_PLATFORMS.map((p, i) => (
              <motion.div
                key={p.name}
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                  activePlatform === i ? 'border-primary scale-110 shadow-lg' : 'border-border opacity-50'
                }`}
                style={{ backgroundColor: activePlatform === i ? p.bg : 'transparent' }}
                whileHover={{ scale: 1.15 }}
              >
                <Icon name={p.icon} size={20} color={activePlatform === i ? (p.color || undefined) : 'var(--color-muted-foreground)'} />
              </motion.div>
            ))}
          </motion.div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight">
            <span className="block">Turn your technical ideas into</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={activePlatform}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-3"
                style={{ color: HERO_PLATFORMS[activePlatform].color || 'var(--color-foreground)' }}
              >
                <Icon name={HERO_PLATFORMS[activePlatform].icon} size={44} color={HERO_PLATFORMS[activePlatform].color || undefined} />
                {HERO_PLATFORMS[activePlatform].name}
              </motion.span>
            </AnimatePresence>{' '}
            <span className="text-foreground">posts</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
            Paste rough notes. Get polished, platform-ready posts in seconds. Publish to LinkedIn and X with one click.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button size="lg" onClick={() => { trackEvent('cta_click', { button_text: 'Start Publishing Free', button_location: 'hero' }); handleStartPublishing(); }} className="px-8 py-6 text-lg">
                Start Publishing — It's Free
              </Button>
            </motion.div>
          </div>

          {/* Powered by */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className="text-sm text-muted-foreground">Powered by</span>
            {/* OpenAI logo */}
            <svg className="h-5 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 0011.735.4a6.046 6.046 0 00-5.79 4.22 5.985 5.985 0 00-3.997 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0014.235 24a6.046 6.046 0 005.79-4.22 5.985 5.985 0 003.997-2.9 6.046 6.046 0 00-.743-7.097zM14.235 22.224a4.497 4.497 0 01-2.886-1.04l.143-.08 4.794-2.769a.78.78 0 00.395-.678v-6.76l2.027 1.17a.071.071 0 01.039.052v5.594a4.516 4.516 0 01-4.512 4.511zM3.953 18.13a4.49 4.49 0 01-.538-3.023l.143.085 4.794 2.769a.78.78 0 00.788 0l5.853-3.381v2.34a.07.07 0 01-.028.061l-4.846 2.797a4.516 4.516 0 01-6.166-1.648zM2.637 7.903a4.49 4.49 0 012.348-1.98v5.712a.78.78 0 00.395.679l5.853 3.38-2.027 1.17a.07.07 0 01-.067.005L4.293 14.07a4.516 4.516 0 01-1.656-6.166zm16.17 3.762l-5.852-3.38 2.027-1.17a.07.07 0 01.067-.006l4.846 2.798a4.506 4.506 0 01-.695 8.132v-5.695a.78.78 0 00-.394-.679zm2.017-3.03l-.143-.085-4.794-2.77a.78.78 0 00-.788 0l-5.853 3.381V6.822a.07.07 0 01.028-.06l4.846-2.798a4.511 4.511 0 016.704 4.67zm-12.68 4.173l-2.028-1.17a.071.071 0 01-.039-.053V6.49a4.511 4.511 0 017.398-3.462l-.143.08-4.794 2.77a.78.78 0 00-.395.678zm1.101-2.37l2.607-1.505 2.607 1.505v3.01l-2.607 1.505-2.607-1.505z"/>
            </svg>
            <span className="text-muted-foreground text-sm">&</span>
            {/* Anthropic logo */}
            <svg className="h-5 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.304 3.541h-3.483l6.196 16.918h3.483L17.304 3.541zm-10.608 0L.5 20.459h3.544l1.26-3.474h6.392l1.26 3.474h3.544L10.304 3.541H6.696zm-.465 10.737L8.5 8.142l2.269 6.136H6.231z"/>
            </svg>
          </div>
        </motion.div>
      </motion.section>

      {/* ─── Product Showcase ─── */}
      <ProductShowcase />

      {/* ─── Who It's For ─── */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Built for everyone in tech who wants to build a personal brand</h2>
            <p className="text-muted-foreground text-lg mb-8">You already have the ideas. POSTRequest turns them into content that gets noticed.</p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {ROLES.map((role, i) => (
                <motion.span
                  key={role}
                  className="px-4 py-2 bg-card border border-border rounded-full text-sm font-medium text-foreground hover:border-primary/40 transition-colors"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                >
                  {role}
                </motion.span>
              ))}
              <span className="px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary">
                + anyone shipping code or products
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">From rough idea to published post</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Three steps. No writer&apos;s block.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <motion.div key={step.num}
                className={`relative bg-card rounded-2xl p-8 border-2 transition-all duration-300 ${activeStep === i ? 'border-primary/50 shadow-lg' : 'border-border'}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                onClick={() => setActiveStep(i)}
              >
                <div className="w-12 h-12 rounded-full text-white text-xl font-bold flex items-center justify-center mb-5"
                  style={{ backgroundColor: step.color === 'zinc' ? '#52525B' : step.color === 'purple' ? '#a855f7' : '#22c55e' }}>
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">{step.desc}</p>

                {step.examples && (
                  <div className="space-y-2">
                    {step.examples.map((ex, j) => (
                      <div key={j} className="flex items-start gap-2 text-sm text-muted-foreground font-mono bg-muted/50 rounded-lg px-3 py-2">
                        <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                        <span>{ex}</span>
                      </div>
                    ))}
                  </div>
                )}

                {step.bullets && (
                  <div className="space-y-2">
                    {step.bullets.map((b, j) => (
                      <div key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                        {b}
                      </div>
                    ))}
                  </div>
                )}

                {step.platforms && (
                  <div className="grid grid-cols-2 gap-2">
                    {PLATFORMS_ALL.map((p) => (
                      <div key={p.name} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
                        <Icon name={p.icon} size={18} color={p.color} />
                        <span className="text-xs text-muted-foreground font-medium">{p.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {i < 2 && (
                  <motion.div className="hidden md:block absolute top-1/2 -right-5 z-10"
                    animate={{ x: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                    <Icon name="ArrowRight" size={24} color="var(--color-primary)" />
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {[0, 1, 2].map((s) => (
              <button key={s} onClick={() => setActiveStep(s)}
                className={`h-2.5 rounded-full transition-all duration-300 ${activeStep === s ? 'bg-primary w-8' : 'bg-muted-foreground/25 w-2.5'}`}
                aria-label={`Step ${s + 1}`} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Not ChatGPT? ─── */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">How POSTRequest is different from ChatGPT</h2>
            <p className="text-muted-foreground text-lg">ChatGPT generates text. POSTRequest publishes content.</p>
          </motion.div>

          <motion.div
            className="bg-card border border-border rounded-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1fr_120px_120px] border-b border-border">
              <div className="px-5 py-4">
                <span className="text-sm font-medium text-muted-foreground">Feature</span>
              </div>
              <div className="px-3 py-4 text-center border-l border-primary/20 bg-primary/5">
                <span className="text-sm font-semibold text-foreground">POSTRequest</span>
              </div>
              <div className="px-3 py-4 text-center border-l border-border">
                <span className="text-sm font-medium text-muted-foreground">ChatGPT</span>
              </div>
            </div>

            {/* Table Rows */}
            {COMPARISON.map((row, i) => (
              <div key={i} className={`grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1fr_120px_120px] ${i < COMPARISON.length - 1 ? 'border-b border-border' : ''}`}>
                <div className="px-5 py-4">
                  <span className="text-sm font-medium text-foreground">{row.feature}</span>
                  <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">{row.desc}</p>
                </div>
                <div className="px-3 py-4 flex items-center justify-center border-l border-primary/20 bg-primary/5">
                  <Icon name="CheckCircle" size={18} className="text-emerald-500" />
                </div>
                <div className="px-3 py-4 flex items-center justify-center border-l border-border">
                  {row.them === 'partial' ? (
                    <Icon name="AlertCircle" size={18} className="text-amber-500" />
                  ) : (
                    <Icon name="XCircle" size={18} className="text-red-400/60" />
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Simple, transparent pricing</h2>
            <p className="text-muted-foreground text-lg">Start free. Upgrade when you need more.</p>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Table Header — plan names + pricing + CTA */}
            <div className="grid grid-cols-3 border-b border-border">
              <div className="p-5 flex items-end">
                <p className="text-xs text-muted-foreground">Compare what you get on each plan.</p>
              </div>

              {/* Basic */}
              <div className="p-5 border-l border-border">
                <h3 className="text-lg font-semibold text-foreground mb-0.5">Basic</h3>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground text-sm ml-1">/month</span>
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={handleStartPublishing}>
                  Get Started
                </Button>
              </div>

              {/* Pro */}
              <div className="p-5 border-l border-violet-500/30 bg-violet-500/5 relative">
                <div className="absolute -top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA]" />
                <div className="flex items-center gap-2 mb-0.5">
                  <h3 className="text-lg font-semibold text-foreground">Pro</h3>
                  <span className="bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white text-[9px] font-semibold px-2 py-0.5 rounded-full">Popular</span>
                </div>
                <div className="mb-3">
                  <span className="text-2xl font-bold text-foreground">₹419</span>
                  <span className="text-muted-foreground text-sm ml-1">/month</span>
                  <p className="text-[10px] text-muted-foreground mt-0.5">≈ $5/month</p>
                </div>
                <Button size="sm" className="w-full" onClick={handleStartPublishing}>
                  Start Free, Upgrade Anytime
                </Button>
              </div>
            </div>

            {/* Feature Rows */}
            {PRICING_FEATURES.map((f, i) => (
              <div key={i} className={`grid grid-cols-3 ${i < PRICING_FEATURES.length - 1 ? 'border-b border-border' : ''}`}>
                <div className="px-5 py-3.5 flex items-center">
                  <span className="text-sm text-foreground font-medium">{f.name}</span>
                </div>
                <div className="px-5 py-3.5 border-l border-border flex items-center justify-center">
                  {f.free === true ? (
                    <Icon name="Check" size={16} className="text-emerald-500" />
                  ) : f.free === false ? (
                    <Icon name="Minus" size={16} className="text-muted-foreground" />
                  ) : (
                    <span className="text-sm text-muted-foreground">{f.free}</span>
                  )}
                </div>
                <div className="px-5 py-3.5 border-l border-violet-500/30 bg-violet-500/5 flex items-center justify-center">
                  {f.pro === true ? (
                    <Icon name="Check" size={16} className="text-violet-400" />
                  ) : f.pro === 'coming soon' ? (
                    <span className="text-xs text-violet-400/60">Coming soon</span>
                  ) : (
                    <span className="text-sm text-foreground font-medium">{f.pro}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Frequently asked questions</h2>
          </motion.div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <motion.div key={i}
                className="bg-card border border-border rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors"
                >
                  <span className="font-medium text-foreground pr-4">{faq.q}</span>
                  <Icon name={openFaq === i ? 'ChevronUp' : 'ChevronDown'} size={18} className="flex-shrink-0 text-muted-foreground" />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p className="px-5 pb-5 text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="bg-gradient-to-br from-primary/10 to-accent/10 border-t border-primary/10 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Your next opportunity is one post away
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join people in tech who are turning their daily work into content that builds their reputation.
            </p>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Button size="lg" onClick={() => { trackEvent('cta_click', { button_text: 'Get Started Free', button_location: 'final_cta' }); handleStartPublishing(); }} className="px-10 py-6 text-lg">
                Get Started Free
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <Icon name="Send" size={18} className="text-white" />
                </div>
                <span className="text-lg font-bold text-foreground">POSTRequest</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
                Turn your tech experience into content that builds your personal brand. Write once, publish everywhere.
              </p>
              <div className="flex gap-3 mt-4">
                {PLATFORMS_ALL.map((p) => (
                  <div key={p.name} className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center ${p.comingSoon ? 'opacity-50' : ''}`}>
                    <Icon name={p.icon} size={16} color={p.color} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={handleStartPublishing} className="hover:text-foreground transition-colors">Get Started</button></li>
                <li><span className="cursor-default">Features</span></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-foreground transition-colors">Pricing</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="mailto:support@postrequest.social" className="hover:text-foreground transition-colors">Contact</a></li>
                <li><Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} POSTRequest. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Built for people in tech, by people in tech.
            </p>
          </div>
        </div>
      </footer>

      {/* ─── Login Modal ─── */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="bg-card rounded-2xl shadow-2xl p-8 border border-border max-w-md w-full relative"
              onClick={(e) => e?.stopPropagation()}
            >
              <button onClick={() => setShowLoginModal(false)}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="XCircle" size={24} />
              </button>

              <div className="mb-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                  <Icon name="Send" size={24} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Get Started with POSTRequest
                </h2>
                <p className="text-sm text-muted-foreground">
                  Sign in to start creating and publishing content
                </p>
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
              )}

              <Button type="button" className="w-full flex items-center justify-center gap-3 h-12 text-base"
                onClick={() => { trackEvent('google_signin_click', { button_location: 'login_modal' }); handleGoogleSignIn(); }}
                disabled={loading}>
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Connecting...
                  </div>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#FFFFFF" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#FFFFFF" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.87.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FFFFFF" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#FFFFFF" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>

              <p className="mt-4 text-[10px] text-center text-muted-foreground">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-primary hover:underline">Terms</a> and{' '}
                <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuthenticationLanding;
